const CHECKLIST_FOOTER =
  "Work through these flexibly. If a step is already done or doesn't apply " +
  "to the current page state, skip it. " +
  "Call `mark_goal_complete` with a summary of what you accomplished for each step. " +
  "Call `mark_goal_failed` only if you cannot make progress on ANY of the remaining steps.";

const VERIFY_SYSTEM_HINT =
  "You are a strict QA assertion agent. Check whether the condition described " +
  "below is met on the page, and PASS or FAIL accordingly.";

const TYPE_TO_PREFIX = {
  verify: "Verify",
  navigate: "Navigate",
  click: "Click",
  type: "Type",
  wait: "Wait",
  text_present: "Text Present",
  element_exists: "Element Exists",
};

/**
 * Groups test-case items into execution batches, mirroring the orchestrator
 * (orchestrator.py lines 207-220). Consecutive agent_multistep items are
 * merged into a single action_batch; everything else stays individual.
 */
export function buildBatches(items) {
  const batches = [];
  let currentActionBatch = [];

  (items || []).forEach((item, i) => {
    if (item.item_type === "agent_multistep") {
      currentActionBatch.push({ index: i, item });
    } else {
      if (currentActionBatch.length > 0) {
        batches.push({ type: "action_batch", items: [...currentActionBatch] });
        currentActionBatch = [];
      }
      batches.push({ type: "single", items: [{ index: i, item }] });
    }
  });

  if (currentActionBatch.length > 0) {
    batches.push({ type: "action_batch", items: [...currentActionBatch] });
  }

  return batches;
}

/**
 * Converts structured test cases into the markdown prompts that the
 * orchestrator actually sends to the LLM, including proper batching
 * and separate verify-agent sections.
 */
export function testCasesToMarkdown(testCases) {
  if (!testCases || testCases.length === 0) return "";

  return testCases
    .map((tc) => {
      const parts = [];

      parts.push(`# Test Case: ${tc.name || "Untitled"}`);

      const meta = [];
      if (tc.category) meta.push(`**Category:** ${tc.category}`);
      meta.push(`**URL:** ${tc.url_placeholder || "root_url"}`);
      if (tc.id) meta.push(`**ID:** ${tc.id}`);
      if (tc.activated === false) meta.push("**Status:** disabled");
      parts.push(meta.join(" | "));

      const batches = buildBatches(tc.items || []);

      for (const batch of batches) {
        if (batch.type === "action_batch") {
          parts.push("---");

          if (batch.items.length > 1) {
            parts.push("**\\[Action Agent\\]**");
            parts.push(
              "Complete the following steps in order. Adapt your approach based on what you see on the page.\n"
            );
            parts.push("## Checklist\n");
            batch.items.forEach(({ item }, j) => {
              const title = item.title || `Step ${j + 1}`;
              parts.push(`### Step ${j + 1}: ${title}\n${item.objective || ""}`);
            });
            parts.push("---");
            parts.push(CHECKLIST_FOOTER);
          } else {
            const { item } = batch.items[0];
            const title = item.title || "Action";
            parts.push("**\\[Action Agent\\]**");
            parts.push(`### ${title}\n${item.objective || ""}`);
          }
        } else {
          const { item } = batch.items[0];

          if (item.item_type === "verify") {
            parts.push("---");
            parts.push("**\\[Verify Agent\\]**");
            parts.push(`> *${VERIFY_SYSTEM_HINT}*`);
            const title = item.title || "Verification";
            parts.push(`### ${title}\n${item.objective || ""}`);
          } else {
            const prefix = TYPE_TO_PREFIX[item.item_type] || item.item_type;
            const title = item.title || prefix;
            parts.push("---");
            parts.push(`**\\[${prefix}\\]**`);
            parts.push(`### ${title}\n${item.objective || ""}`);
          }
        }
      }

      return parts.join("\n\n");
    })
    .join("\n\n");
}
